<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte';
    import { querystring, replace } from 'svelte-spa-router';
    import BillListSkeleton from '../../lib/Bills/BillListSkeleton.svelte';
    import BillRow from './BillRow.svelte';
    import {
        billDetailRoute,
        billsSettings,
        defaultGovernmentUri,
        filterSpotlights,
        findSpotlight,
        getCategoryNames,
        getKeyLegislationGovernments,
        groupSpotlightsByCategory,
        parseLegacyBillHash,
        type BillGovernment,
        type BillPosition,
        type BillSpotlight
    } from '../../api/bills';
    import { billSearch, billsFilters, billsLastBillUri, billsListedUrls, billsListScroll, loadBillsContext, loadMoreBillSearchResults, runBillSearch } from './billsState';
    import { positionLabel, positions, positionStyles } from '../../lib/Bills/billPositions';
    import * as t from '../../translations/Bills.ts';

    const UNCATEGORIZED = '__uncategorized__';

    let loading = true;
    let error = '';
    let spotlights: BillSpotlight[] = [];
    let governments: BillGovernment[] = [];
    let keyLegislationGovernments: BillGovernment[] = [];
    let defaultSearchGovernmentUri = '';
    let defaultKeyLegislationGovernmentUri = '';
    let keywords = $billSearch.keywords;

    onMount(() => {
        billsLastBillUri.value = '';
        const legacyBillUrl = parseLegacyBillHash($querystring);
        if (legacyBillUrl) {
            replace(billDetailRoute(legacyBillUrl));
            return;
        }
        void load().then(restoreScroll);
    });

    onDestroy(() => {
        billsListScroll.y = window.scrollY;
    });

    async function restoreScroll() {
        if (!billsListScroll.y) return;
        await tick();
        window.scrollTo({ top: billsListScroll.y });
        billsListScroll.y = 0;
    }

    async function load() {
        loading = true;
        error = '';
        try {
            ({ spotlights, governments } = await loadBillsContext());
            keyLegislationGovernments = getKeyLegislationGovernments(governments, spotlights);
            defaultSearchGovernmentUri = defaultGovernmentUri(governments, billsSettings.defaultGovernmentName);
            defaultKeyLegislationGovernmentUri = defaultGovernmentUri(keyLegislationGovernments, billsSettings.defaultGovernmentName);
            if (!$billsFilters.initialized) {
                $billsFilters = {
                    ...$billsFilters,
                    searchGovernmentUri: defaultSearchGovernmentUri,
                    keyLegislationGovernmentUri: defaultKeyLegislationGovernmentUri,
                    initialized: true
                };
                const initialSearch = billsSettings.initialSearch?.trim();
                if (initialSearch) {
                    keywords = initialSearch;
                    void runBillSearch($billsFilters.searchGovernmentUri, initialSearch);
                }
            }
        } catch (e) {
            console.error('[Bills] Failed to load bills:', e);
            error = t.Loading_Error();
        } finally {
            loading = false;
        }
    }

    function matchesCategory(bill: BillSpotlight, category: string): boolean {
        if (!category) return true;
        return category === UNCATEGORIZED ? !bill.categoryName : bill.categoryName === category;
    }

    $: category = $billsFilters.category;
    $: position = $billsFilters.position;
    $: inJurisdiction = filterSpotlights(spotlights, $billsFilters.keyLegislationGovernmentUri, '', 'all');
    $: categoryNames = getCategoryNames(inJurisdiction);
    $: uncategorizedCount = inJurisdiction.filter((s) => !s.categoryName).length;
    $: categoryTabs =
        categoryNames.length === 0
            ? []
            : [
                  { key: '', label: t.All_Tab(), count: inJurisdiction.length },
                  ...categoryNames.map((name) => ({
                      key: name,
                      label: name,
                      count: inJurisdiction.filter((s) => s.categoryName === name).length
                  })),
                  ...(uncategorizedCount > 0 ? [{ key: UNCATEGORIZED, label: t.Uncategorized(), count: uncategorizedCount }] : [])
              ];
    $: if (!loading && category && !categoryTabs.some((tab) => tab.key === category)) {
        billsFilters.update((f) => ({ ...f, category: '' }));
    }
    $: inCategory = inJurisdiction.filter((s) => matchesCategory(s, category));
    $: positionCounts = Object.fromEntries(
        positions.map((p) => [p, inCategory.filter((s) => s.position === p).length])
    ) as Record<BillPosition, number>;
    $: visible = position === 'all' ? inCategory : inCategory.filter((s) => s.position === position);
    $: showGroupHeaders = !category && categoryNames.length > 0;
    $: groups = showGroupHeaders ? groupSpotlightsByCategory(visible, categoryNames) : [{ category: null, items: visible }];

    $: searchActive = !!$billSearch.keywords;
    $: if (!loading) {
        billsListedUrls.set(
            searchActive
                ? $billSearch.items.map((i) => i.url)
                : groups.flatMap((g) => g.items.map((s) => s.legislativeProposalUrl))
        );
    }
    $: isDefaultView =
        !category &&
        position === 'all' &&
        $billsFilters.keyLegislationGovernmentUri === defaultKeyLegislationGovernmentUri &&
        $billsFilters.searchGovernmentUri === defaultSearchGovernmentUri;
    $: searchGovernmentName = governments.find((g) => g.uri === $billSearch.governmentUri)?.name ?? '';
    $: searchSessionLabel =
        $billSearch.sessionNames.length === 1
            ? $billSearch.sessionNames[0]
            : $billSearch.sessionNames.length > 1
              ? t.Multiple_Sessions()
              : '';

    function submitSearch() {
        const trimmed = keywords.trim();
        if (!trimmed) {
            clearSearch();
            return;
        }
        (document.activeElement as HTMLElement | null)?.blur();
        void runBillSearch($billsFilters.searchGovernmentUri, trimmed);
    }

    function clearSearch() {
        keywords = '';
        void runBillSearch('', '');
    }

    function resetView() {
        clearSearch();
        $billsFilters = {
            ...$billsFilters,
            searchGovernmentUri: defaultSearchGovernmentUri,
            keyLegislationGovernmentUri: defaultKeyLegislationGovernmentUri,
            category: '',
            position: 'all'
        };
    }

    function togglePosition(p: BillPosition) {
        $billsFilters.position = position === p ? 'all' : p;
    }

    function handleSearchKeydown(evt: KeyboardEvent) {
        if (evt.key === 'Escape' && keywords) {
            evt.preventDefault();
            clearSearch();
        }
    }

    const focusRing =
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#2563eb)] focus-visible:ring-offset-2';
    const textButton = `rounded-md !border-0 !bg-transparent !px-1 text-sm font-medium !text-gray-600 hover:!text-gray-900 ${focusRing}`;
</script>

<div class="bills-page w-full m-auto max-w-[1000px] py-4 px-4 md:px-8 flex flex-col gap-8 interfont" data-testid="bills-index">
    <header class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 class="m-0 text-3xl text-gray-900 md:text-4xl">{t.Page_Title()}</h1>

        <form
            role="search"
            class="m-0 flex w-full items-center rounded-xl border border-gray-300 bg-white shadow-sm transition-shadow
                focus-within:border-[var(--vv-action-center-button-color,#2563eb)] focus-within:ring-1 focus-within:ring-[var(--vv-action-center-button-color,#2563eb)] md:max-w-xl"
            on:submit|preventDefault={submitSearch}
            data-testid="bills-search"
        >
            <label for="bills-search-jurisdiction" class="sr-only">{t.Jurisdiction_Label()}</label>
            <span class="relative flex h-11 shrink-0 items-center border-0 border-r border-solid border-gray-200">
                <select
                    id="bills-search-jurisdiction"
                    class="!m-0 !h-full max-w-[8.5rem] cursor-pointer !appearance-none !rounded-l-xl !rounded-r-none !border-0 !bg-transparent !bg-none !pl-3 !pr-8 !text-sm !font-medium !text-gray-800 [field-sizing:content] focus:outline-none focus:ring-0 sm:max-w-[10rem]"
                    bind:value={$billsFilters.searchGovernmentUri}
                    disabled={loading || governments.length === 0}
                >
                    {#each governments as gov (gov.uri)}
                        <option value={gov.uri}>{gov.name}</option>
                    {/each}
                </select>
                <svg class="pointer-events-none absolute right-3 h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
            </span>
            <svg class="ml-3 h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
                id="bills-search-keywords"
                type="search"
                autocomplete="off"
                class="!m-0 !h-11 min-w-0 flex-1 !rounded-none !border-0 !bg-transparent !px-2 !text-sm !font-normal !text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#fff] [&::-webkit-search-cancel-button]:appearance-none"
                placeholder={t.Search_Placeholder()}
                aria-label={t.Search_Placeholder()}
                bind:value={keywords}
                disabled={loading}
                on:keydown={handleSearchKeydown}
            />
            {#if keywords}
                <button
                    type="button"
                    class="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full !border-0 !bg-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-700 {focusRing}"
                    aria-label={t.Clear_Search()}
                    on:click={clearSearch}
                >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            {/if}
            <button
                type="submit"
                class="m-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg !border-0 bg-[var(--vv-action-center-button-color,#2563eb)] text-sm font-semibold text-[color:var(--vv-action-center-button-text,#fff)] hover:opacity-90 disabled:opacity-60 sm:w-auto sm:!px-4 {focusRing}"
                disabled={loading || $billSearch.loading}
            >
                <svg class="h-4 w-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span class="sr-only sm:not-sr-only">{t.Search_Button()}</span>
            </button>
        </form>
    </header>

    {#if searchActive}
        <section class="mt-2" data-testid="bills-search-results" aria-busy={$billSearch.loading}>
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div class="min-w-0">
                    <h2 class="bills-strong m-0 text-xl text-gray-900">
                        {t.Search_Results_For($billSearch.keywords, searchGovernmentName)}
                    </h2>
                    {#if !$billSearch.loading && !($billSearch.error && $billSearch.items.length === 0)}
                        <p class="mt-1 text-sm text-gray-500" aria-live="polite" data-testid="bills-search-count">
                            {$billSearch.totalResults > 0
                                ? t.Search_Results_Count(String($billSearch.items.length), String($billSearch.totalResults))
                                : t.Search_Results_None()}
                            {#if searchSessionLabel}<span> · {searchSessionLabel}</span>{/if}
                        </p>
                    {/if}
                </div>
                <button type="button" class="inline-flex items-center gap-1.5 {textButton}" on:click={clearSearch} data-testid="bills-back-to-key">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    {t.Back_To_Key_Legislation()}
                </button>
            </div>

            {#if $billSearch.loading}
                <BillListSkeleton count={4} />
            {:else if $billSearch.error && $billSearch.items.length === 0}
                <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{t.Search_Error()}</div>
            {:else if $billSearch.items.length === 0}
                <p class="m-0 rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-600">{t.Search_Results_Empty()}</p>
            {:else}
                <ul class="m-0 list-none divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white p-0">
                    {#each $billSearch.items as item (item.url)}
                        <li class="m-0 p-0">
                            <BillRow
                                name={item.display}
                                position={findSpotlight(spotlights, item.url)?.position ?? null}
                                href={billDetailRoute(item.url)}
                            />
                        </li>
                    {/each}
                </ul>
                {#if $billSearch.error}
                    <p class="mt-3 text-sm text-red-700" role="alert">{t.Search_Error()}</p>
                {/if}
                {#if $billSearch.continuationToken}
                    <div class="mt-4 flex justify-center">
                        <button
                            type="button"
                            class="rounded-lg !border !border-gray-300 bg-white !px-4 !py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 {focusRing}"
                            disabled={$billSearch.loadingMore}
                            on:click={() => loadMoreBillSearchResults($billSearch)}
                        >
                            {$billSearch.loadingMore ? t.Loading() : t.Show_More()}
                        </button>
                    </div>
                {/if}
            {/if}
        </section>
    {:else if loading || error || spotlights.length > 0}
        <section class="mt-2" data-testid="bills-key-legislation">
            <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
                <div class="mr-auto flex flex-wrap items-center gap-x-2 gap-y-2">
                    <h2 class="bills-strong m-0 text-lg text-gray-900 sm:text-xl">{t.Key_Legislation_Header()}</h2>
                    {#if keyLegislationGovernments.length > 1}
                        <span class="text-lg text-gray-500 sm:text-xl" aria-hidden="true">{t.Key_Legislation_In()}</span>
                        <label for="bills-key-jurisdiction" class="sr-only">{t.Jurisdiction_Label()}</label>
                        <span class="relative inline-flex items-center">
                            <select
                                id="bills-key-jurisdiction"
                                class="!m-0 !h-auto min-w-[8rem] max-w-[12rem] cursor-pointer !appearance-none !rounded-lg !border !border-solid !border-gray-300 !bg-white !bg-none !py-2 !pl-3 !pr-9 !text-sm !font-normal !text-gray-900 [field-sizing:content]
                                    focus:border-[var(--vv-action-center-button-color,#2563eb)] focus:ring-2 focus:ring-[var(--vv-action-center-button-color,#2563eb)]"
                                bind:value={$billsFilters.keyLegislationGovernmentUri}
                                data-testid="bills-key-jurisdiction"
                            >
                                {#each keyLegislationGovernments as gov (gov.uri)}
                                    <option value={gov.uri}>{gov.name}</option>
                                {/each}
                            </select>
                            <svg class="pointer-events-none absolute right-3 h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </span>
                    {:else if keyLegislationGovernments.length === 1}
                        <span class="text-xl text-gray-500">{t.Key_Legislation_In()} {keyLegislationGovernments[0].name}</span>
                    {/if}
                </div>
                    {#if !loading && !isDefaultView}
                        <button type="button" class="inline-flex items-center gap-1.5 {textButton}" on:click={resetView} data-testid="bills-reset">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {t.Reset_View()}
                        </button>
                    {/if}
                    {#if billsSettings.spotlightsCsvUrl && !loading && !error}
                        <a href={billsSettings.spotlightsCsvUrl} class="inline-flex items-center gap-1.5 no-underline {textButton}">
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            {t.Download_Csv()}
                        </a>
                    {/if}
            </div>

            {#if error}
                <div class="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</div>
            {:else if loading}
                <BillListSkeleton count={5} showFilters />
            {:else}
                {#if categoryTabs.length > 0}
                    <div
                        class="mt-4 flex gap-6 overflow-x-auto border-0 border-b border-solid border-gray-200 pr-10 [mask-image:linear-gradient(to_right,#000_80%,transparent)] [scrollbar-width:none] sm:pr-0 sm:[mask-image:none]"
                        role="group"
                        aria-label={t.Category_Label()}
                        data-testid="bills-category-tabs"
                    >
                        {#each categoryTabs as tab (tab.key)}
                            <button
                                type="button"
                                class="-mb-px inline-flex shrink-0 items-center gap-2 whitespace-nowrap !border-0 !border-b-2 border-solid !bg-transparent !px-0 !pb-3 !pt-1 text-sm !font-normal transition-colors {focusRing}
                                    {category === tab.key
                                    ? 'bills-strong !border-gray-900 text-gray-900'
                                    : '!border-transparent text-gray-600 hover:!border-gray-300 hover:text-gray-900'}"
                                aria-pressed={category === tab.key}
                                on:click={() => ($billsFilters.category = tab.key)}
                            >
                                {tab.label}
                                <span
                                    class="inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums
                                        {category === tab.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}"
                                >
                                    {tab.count}
                                </span>
                            </button>
                        {/each}
                    </div>
                {/if}

                <div class="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label={t.Position_Filter_Label()} data-testid="bills-position-filter">
                    {#each positions.filter((p) => positionCounts[p] > 0 || position === p) as p (p)}
                        <button
                            type="button"
                            class="inline-flex items-center gap-2 rounded-full !border !border-solid !px-3 !py-1.5 text-sm !font-normal transition-colors {focusRing}
                                {position === p ? positionStyles[p].active : positionStyles[p].idle}"
                            aria-pressed={position === p}
                            on:click={() => togglePosition(p)}
                        >
                            <span class="h-2 w-2 rounded-full {position === p ? 'bg-white' : positionStyles[p].dot}" aria-hidden="true"></span>
                            {positionLabel(p)}
                            <span class="tabular-nums opacity-70">{positionCounts[p]}</span>
                        </button>
                    {/each}
                </div>

                {#if visible.length === 0}
                    <div class="mt-4 rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center">
                        <p class="m-0 text-sm text-gray-600">{t.Filtered_Empty()}</p>
                        <button type="button" class="mt-2 {textButton} text-[#1b75d0]" on:click={resetView}>{t.Reset_View()}</button>
                    </div>
                {:else}
                    <div class="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="bills-key-list">
                        {#each groups as group, i (group.category ?? UNCATEGORIZED)}
                            <div class={i > 0 ? 'border-0 border-t border-solid border-gray-200' : ''}>
                                {#if showGroupHeaders}
                                    <h3 class="bills-strong m-0 flex items-center gap-2 border-0 border-b border-solid border-gray-100 bg-gray-50 px-4 py-2.5 text-sm text-gray-900">
                                        {group.category ?? t.Uncategorized()}
                                        <span class="text-gray-500">{group.items.length}</span>
                                    </h3>
                                {/if}
                                <ul class="m-0 list-none divide-y divide-gray-100 p-0">
                                    {#each group.items as bill (bill.legislativeProposalUrl)}
                                        <li class="m-0 p-0">
                                            <BillRow
                                                name={bill.name}
                                                session={bill.sessionDisplayName}
                                                position={bill.position}
                                                href={billDetailRoute(bill.legislativeProposalUrl)}
                                            />
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        {/each}
                    </div>
                {/if}
            {/if}
        </section>
    {/if}
</div>

<style>
    /* app.css forces every .interfont descendant onto 'vv-inter', which only has a regular face,
       so Tailwind's font-bold is synthesized. Headings here use the real bold face instead. */
    .bills-strong {
        font-family: 'vv-inter-bold', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    }

    /* Same fixed-size-at-every-breakpoint idea as the Events/Petitions list-page h1 pattern
       (.event-list-page h1 / .petition-list-page h1), but deliberately kept at regular weight --
       Bills is a denser filter/list page, and bold-at-this-size read as too "shouty" here.
       Uses !important, unlike those two, since .vvBody h1 ties this rule on specificity and only
       source order would decide the winner otherwise. */
    :global(.bills-page h1) {
        font-size: 2rem !important;
        line-height: 2.5rem !important;
        color: #111827 !important;
        font-weight: 400 !important;
    }
</style>
